import React, { FormEvent, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import ReCAPTCHA from 'react-google-recaptcha'
import { GoogleReCaptcha } from '../../lib/components'
import { userServices } from '../../services'
import {
  completeEmailVerificationLogin,
  isEmailVerificationLoginData,
} from '../../lib/utils/post-verification-auth'
import { applyPostLoginNavigation } from '../../lib/utils/post-login'
import { useDispatch } from 'react-redux'
import { showBanner } from '../../global-state/banner-slice'
import { captureEvent } from '../../lib/utils/posthogUtils/usePostHog'
import { EVENTS } from '../../lib/utils/constants'
import { PasswordStrengthIndicator } from '../signup-v2/PasswordStrengthIndicator'
import './promote-set-password.scss'

const passwordRegex = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/

const PromoteSetPasswordContainer = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const reRef = useRef<ReCAPTCHA>(null)
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [linkError, setLinkError] = useState('')
  const [formError, setFormError] = useState('')
  const [validating, setValidating] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [code, setCode] = useState('')
  const [userId, setUserId] = useState('')

  useEffect(() => {
    document.title = 'Set your password · Credibila'

    const validateLink = async () => {
      const fullUrl = window.location.href
      const decodedUrl = decodeURIComponent(fullUrl.replace(/&amp;/g, '&'))
      const urlObj = new URL(decodedUrl)
      const linkCode = urlObj.searchParams.get('code')
      const linkUser = urlObj.searchParams.get('user')

      if (!linkCode || !linkUser) {
        setLinkError('This link is invalid. Please request a new one from the signup page.')
        setValidating(false)
        return
      }

      setCode(linkCode)
      setUserId(linkUser)

      const response = await userServices.getIsPromoteSignupLinkValid(linkUser, linkCode)
      if (response?.status !== 200) {
        setLinkError(
          (response?.data && response.data.message) ||
            'This link is invalid or has expired. Please request a new one.',
        )
        setValidating(false)
        return
      }

      setValidating(false)
    }

    void validateLink()
  }, [])

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setFormError('')

    if (!passwordRegex.test(password)) {
      setFormError(
        'Password must be at least 8 characters and include upper, lower, digit and special character.',
      )
      return
    }
    if (password !== passwordConfirmation) {
      setFormError('Passwords do not match.')
      return
    }

    setSubmitting(true)
    const token = await reRef.current?.executeAsync()
    reRef.current?.reset()

    const response = await userServices.completePromoteSignup_v1(
      {
        userId,
        code,
        password,
      },
      { 'g-recaptcha-response': token ? token : '' },
    )

    if (response?.status === 200 && isEmailVerificationLoginData(response.data)) {
      captureEvent(EVENTS.email_verified, { user: userId, source: 'promote' })
      dispatch(
        showBanner({
          text: 'Account opened. Welcome to Credibila.',
          variant: 'success-banner',
        }),
      )
      completeEmailVerificationLogin(dispatch, response.data)
      await applyPostLoginNavigation(
        dispatch,
        navigate,
        response.data.authToken,
        response.data.userId,
        response.data.email,
        response.data.name,
        response.data.acceptedNDA ?? false,
      )
      return
    }

    setFormError(
      (response?.data && response.data.message) ||
        'Something went wrong. Please try again or request a new link.',
    )
    setSubmitting(false)
  }

  if (validating) {
    return (
      <div className='promote-set-password' data-testid='promote-set-password-page'>
        <div className='promote-set-password__card'>
          <img className='promote-set-password__logo' src='/promote/logo-v2.png' alt='Credibila' />
          <p className='promote-set-password__subtitle'>Checking your link…</p>
        </div>
      </div>
    )
  }

  if (linkError) {
    return (
      <div className='promote-set-password' data-testid='promote-set-password-page'>
        <div className='promote-set-password__card'>
          <img className='promote-set-password__logo' src='/promote/logo-v2.png' alt='Credibila' />
          <h1 className='promote-set-password__title'>Link unavailable</h1>
          <p className='promote-set-password__error' data-testid='promote-set-password-link-error'>
            {linkError}
          </p>
          <Link className='promote-set-password__link' to='/promote'>
            Back to signup
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className='promote-set-password' data-testid='promote-set-password-page'>
      <div className='promote-set-password__card'>
        <img className='promote-set-password__logo' src='/promote/logo-v2.png' alt='Credibila' />
        <h1 className='promote-set-password__title'>Set your password</h1>
        <p className='promote-set-password__subtitle'>
          Choose a password to open your Credibila account. This link works once and expires in 24
          hours.
        </p>
        <form onSubmit={handleSubmit} noValidate data-testid='promote-set-password-form'>
          <div className='promote-set-password__field'>
            <label htmlFor='promote-password'>Password</label>
            <input
              id='promote-password'
              type='password'
              autoComplete='new-password'
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid='promote-set-password-input'
            />
            <PasswordStrengthIndicator password={password} />
          </div>
          <div className='promote-set-password__field'>
            <label htmlFor='promote-password-confirm'>Confirm password</label>
            <input
              id='promote-password-confirm'
              type='password'
              autoComplete='new-password'
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              data-testid='promote-set-password-confirm'
            />
          </div>
          {formError ? (
            <p className='promote-set-password__error' data-testid='promote-set-password-error'>
              {formError}
            </p>
          ) : null}
          <GoogleReCaptcha handleChange={() => undefined} size='invisible' reCaptchaRef={reRef} />
          <button
            type='submit'
            className='promote-set-password__btn'
            disabled={submitting}
            data-testid='promote-set-password-submit'
          >
            Open account
          </button>
        </form>
      </div>
    </div>
  )
}

export { PromoteSetPasswordContainer }
